import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { TabObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { HumanizeNumberPipe } from 'app/pipes/humanize.pipe';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  SemantriaConfiguration,
  TaxonomyTargets,
  ConfigurationService,
  BaseConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  IndustryPack,
  IndustryPackService,
} from 'app/providers/semantria-api/industry-pack.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from 'app/providers/semantria-api/language-template.service';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { ReportService } from 'app/providers/storage-api/report.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { ConfigurationUtils } from 'app/utils/configuration.utils';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { BaseConfigurationComponent } from '../base-configuration-component';
import { ConfigurationDetailsTabBase } from '../../components/configuration-details-tab-base';
import { ConfigurationLayer } from '../../components/configuration-layers/configuration-layers.component';
import { ImportConfigurationDialogOptions } from '../../components/import-into-configuration-drawer/import-into-configuration-drawer.component';

export interface ConfigurationLimits {
  entities: {
    used: number;
    total: number;
  };
  sentiment: {
    used: number;
    total: number;
  };
  queries: {
    used: number;
    total: number;
  };
  concept_topics: {
    used: number;
    total: number;
  };
  taxonomy: {
    used: number;
    total: number;
  };
  blacklist: {
    used: number;
    total: number;
  };
}

type TabId =
  | 'layers'
  | 'sentiment'
  | 'queries'
  | 'entities'
  | 'taxonomy'
  | 'concept_topics'
  | 'blacklist';

export interface ConfigVisibiltyOptions {
  showConfigDescription: boolean;
  showConfigTags: boolean;
  id?: string;
}
@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-details',
  templateUrl: './configuration-details.component.html',
  styleUrls: ['./configuration-details.component.scss'],
  standalone: false,
})
export class ConfigurationDetailsComponent
  extends BaseConfigurationComponent
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @ViewChildren('configTab') configTabs: QueryList<ConfigurationDetailsTabBase>;

  configuration: SemantriaConfiguration;
  templateConfiguration: BaseConfiguration;

  isLoading = false;
  isReadOnly = true;
  canImportIntoConfiguration;
  showImportIntoConfiguration;
  showUploadDialog;

  showTestConfigurationDrawer: boolean;
  showConfigurationOptions = false;

  importConfigData: ImportConfigurationDialogOptions;

  tabs: TabObject[];
  activeTabId: TabId = 'sentiment';

  childConfigs: string[];
  configurationLayers: ConfigurationLayer[] = [];
  configurationAncestorLayers: ConfigurationLayer[] = [];
  industryPacks: IndustryPack[] = [];
  languageTemplates: LanguageTemplate[] = [];
  accountLimits: { [key: string]: number } = {};

  configurationLimits: ConfigurationLimits = {
    entities: {
      used: -1,
      total: 0,
    },
    sentiment: {
      used: -1,
      total: 0,
    },
    queries: {
      used: -1,
      total: 0,
    },
    concept_topics: {
      used: -1,
      total: 0,
    },
    taxonomy: {
      used: -1,
      total: 0,
    },
    blacklist: {
      used: -1,
      total: 0,
    },
  };

  configId$ = new ReplaySubject<string>();
  unsub$ = new Subject<void>();

  itemId: string;

  queriesTabLoaded: boolean;
  sentimentTabLoaded: boolean;
  conceptsTabLoaded: boolean;
  entitiesTabLoaded: boolean;
  taxonomyTabLoaded: boolean;
  blacklistTabLoaded: boolean;

  tabChangeInProgress: boolean;
  configurationLayersChanged: boolean;

  taxonomyItemUsage: TaxonomyTargets;

  visibilityOptions: ConfigVisibiltyOptions = { showConfigDescription: true, showConfigTags: true };
  hasTags: boolean;

  configurationsRoute: string;
  private localStorageService = inject(LocalStorageService);

  constructor(
    private semantriaAccountService: SemantriaAccountService,
    private languageTemplateService: LanguageTemplateService,
    private industryPackService: IndustryPackService,
    private humanizePipe: HumanizeNumberPipe,
    private reportService: ReportService,
    private cdr: ChangeDetectorRef,
    protected titleService: Title,
    protected appService: AppService,
    protected authService: AuthService,
    protected systemService: SystemService,
    protected dialogService: DialogService,
    protected alertService: AlertService,
    protected configService: ConfigurationService,
    protected groupService: GroupService,
    protected collectionService: CollectionService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected translocoService: TranslocoService
  ) {
    super(
      titleService,
      appService,
      authService,
      systemService,
      dialogService,
      alertService,
      configService,
      groupService,
      collectionService,
      router,
      route,
      translocoService
    );

    this.route.paramMap
      .pipe(
        takeUntil(this.unsub$),
        map((params) => params.get('id')),
        filter((id) => !!id)
      )
      .subscribe((id) => this.configId$.next(id));

    this.route.queryParamMap
      .pipe(
        takeUntil(this.unsub$),
        map((params) => params.get('itemId')),
        filter((id) => !!id)
      )
      .subscribe((id) => (this.itemId = id));

    this.isLoading = true;
  }

  private getTabText(tabId: TabId) {
    return this.isTabDisabled(tabId)
      ? '-'
      : this.humanizePipe.transform(this.configurationLimits[tabId].used) || '...';
  }

  private getTabs(): TabObject[] {
    const t = this.translocoService.translateObject('configurations.details', {
      'layers-tab': {
        count:
          (this.configurationLimits as any).layers?.used === -1
            ? '-'
            : this.humanizePipe.transform((this.configurationLimits as any).layers?.used) || '...',
      },
      'sentiment-tab': {
        count: this.getTabText('sentiment'),
      },
      'queries-tab': {
        count: this.getTabText('queries'),
      },
      'entities-tab': {
        count: this.getTabText('entities'),
      },
      'taxonomy-tab': {
        count: this.getTabText('taxonomy'),
      },
      'topics-tab': {
        count: this.getTabText('concept_topics'),
      },
      'blacklist-tab': {
        count: this.getTabText('blacklist'),
      },
    });
    const tabs = [
      {
        id: 'sentiment',
        title: t['sentiment-tab'],
        disabled: this.isTabDisabled('sentiment'),
        tooltip: { text: this.getTooltipFor('sentiment') },
      },
      {
        id: 'queries',
        title: t['queries-tab'],
        disabled: this.isTabDisabled('queries'),
        tooltip: { text: this.getTooltipFor('queries') },
      },
      {
        id: 'entities',
        title: t['entities-tab'],
        disabled: this.isTabDisabled('entities'),
        tooltip: { text: this.getTooltipFor('entities') },
      },
      {
        id: 'taxonomy',
        title: t['taxonomy-tab'],
        disabled: this.isTabDisabled('taxonomy'),
        tooltip: { text: this.getTooltipFor('taxonomy') },
      },
      {
        id: 'concept_topics',
        title: t['topics-tab'],
        disabled: this.isTabDisabled('concept_topics'),
        tooltip: { text: this.getTooltipFor('concept_topics') },
      },
      {
        id: 'blacklist',
        title: t['blacklist-tab'],
        disabled: this.isTabDisabled('blacklist'),
        tooltip: { text: this.getTooltipFor('blacklist') },
      },
    ];
    if (this.configurationLayers.length > 0) {
      tabs.unshift({
        id: 'layers',
        title: t['layers-tab'],
        disabled: this.isTabDisabled('layers'),
        tooltip: { text: this.getTooltipFor('layers') },
      });
    }
    return tabs;
  }

  async ngOnInit() {
    await super.onInit();
    this.isReadOnly = this.authService.isReadOnly;
    this.cdr.detectChanges();
    this.accountLimits = (await this.semantriaAccountService.sub.latest()).limits;
    this.industryPacks = await this.industryPackService.getIndustryPacks();
    this.languageTemplates = await this.languageTemplateService.getLanguageTemplates();

    this.configurationLimits.entities.total = this.accountLimits.USER_ENTITIES_LIMIT;
    this.configurationLimits.sentiment.total = this.accountLimits.SENTIMENT_PHRASES_LIMIT;
    this.configurationLimits.queries.total = this.accountLimits.QUERY_TOPICS_LIMIT;
    this.configurationLimits.concept_topics.total = this.accountLimits.CONCEPT_TOPICS_LIMIT;
    this.configurationLimits.taxonomy.total = this.accountLimits.TAXONOMY_LIMIT;
    this.configurationLimits.blacklist.total = this.accountLimits.BLACKLISTS_LIMIT;

    this.tabs = [
      { id: 'layers', title: '' },
      { id: 'sentiment', title: '' },
      { id: 'queries', title: '' },
      { id: 'entities', title: '' },
      { id: 'taxonomy', title: '' },
      { id: 'concept_topics', title: '' },
      { id: 'blacklist', title: '' },
    ];

    this.configId$.pipe(takeUntil(this.unsub$)).subscribe(async (configId) => {
      this.isLoading = true;

      let config;
      try {
        config = await this.configService.getConfiguration(configId);
      } catch (err) {
        // Load a (possibly) deleted configuration
        this.configurations =
          this.configurations || (await this.configService.getConfigurations(true));
        config = this.configurations.find((c) => c.id === configId);
      }

      if (!config) {
        this.isLoading = false;
        this.router.navigateByUrl('not-found', { skipLocationChange: true });
        return;
      }

      config = this.initConfigData(config);

      if (config.cloned_from) {
        const templateConfig =
          this.industryPacks.find((ip) => ip.id === config.cloned_from) ||
          this.languageTemplates.find((lt) => lt.id === config.cloned_from);
        if (templateConfig) {
          this.templateConfiguration = templateConfig;
        } else {
          this.configService
            .getConfiguration(config.cloned_from)
            .then((res) => {
              if (res) {
                this.templateConfiguration = res;
              }
            })
            .catch(() => {
              //This is fine just means it's an industry pack or language template so will get filled in inside
              //the configuration-overview.component.
            });
        }
      }

      if (config.layer) {
        const collectParents = async (parents) => {
          const layers = [];

          for (const p of parents) {
            let layer: any = this.industryPacks.find((ip) => ip.id === p);
            if (layer) {
              layers.push({ ...layer, isIndustryPack: true });
              continue;
            }

            layer = this.languageTemplates.find((lt) => lt.id === p);
            if (layer) {
              layers.push({ ...layer, isLanguageTemplate: true });
              continue;
            }

            layer = await this.configService.getConfiguration(p);
            layers.push({ ...layer, isConfiguration: true });
          }

          return layers;
        };

        this.configurationLayers = await collectParents(config.parents);
        const ancestors = [...this.configurationLayers];

        if (!config.removed) {
          const parentTree = await this.configService.getConfigurationParents(config.id, 'tree');

          const traverseAncestorTree = async (parents, level = 0) => {
            const layers = [];

            if (level !== 0) {
              layers.push(...(await collectParents(parents.map((par) => par.id))));
            }

            for (const p of parents) {
              if (p.parents) {
                layers.push(...(await traverseAncestorTree(p.parents, ++level)));
              }
            }

            return layers;
          };

          ancestors.push(...(await traverseAncestorTree(parentTree.tree.parents)));
          this.configurationAncestorLayers = [...new Map(ancestors.map((a) => [a.id, a])).values()];
        }
      }
      this.visibilityOptions = this.getVisibilityOptions(config);

      if (!config.removed) {
        this.configService
          .getTaxonomyItems(config.id)
          .then((res) => (this.taxonomyItemUsage = res));

        this.configService.getConfigurationItemCounts(config.id).then((res) => {
          this.configurationLimits.entities.used = res.user_entities_count;
          this.configurationLimits.sentiment.used = res.sentiment_phrases_count;
          this.configurationLimits.queries.used = res.query_topic_count;
          this.configurationLimits.concept_topics.used = res.concept_topics_count;
          this.configurationLimits.taxonomy.used = res.taxonomy_count;
          this.configurationLimits.blacklist.used = res.blacklist_count;
          if (this.configurationLayers.length > 0) {
            (this.configurationLimits as any).layers = {
              used: config.parents.length,
              total: this.accountLimits.CONFIGURATION_LAYER_PARENTS_LIMIT,
            };
          }

          this.configService
            .getConfigurationChildren(config.id)
            .then((childRes) => (this.childConfigs = childRes));

          this.configuration = config;
          this.tabs = this.getTabs();
        });
      } else {
        this.configurationLimits.entities.used = 0;
        this.configurationLimits.sentiment.used = 0;
        this.configurationLimits.queries.used = 0;
        this.configurationLimits.concept_topics.used = 0;
        this.configurationLimits.taxonomy.used = 0;
        this.configurationLimits.blacklist.used = 0;

        this.configuration = config;
        this.tabs = this.getTabs();
      }

      this.isLoading = false;

      this.route.fragment.pipe(takeUntil(this.unsub$)).subscribe((fragment) => {
        const activeTab = this.tabs.find((t) => t.id.includes(fragment));
        const fallbackTabId = this.configurationLayers.length > 0 ? 'layers' : 'sentiment';
        this.activeTabId = (activeTab?.id ?? fallbackTabId) as TabId;

        this.isLoading = false;

        switch (this.activeTabId) {
          case 'sentiment':
            this.sentimentTabLoaded = true;
            break;
          case 'queries':
            this.queriesTabLoaded = true;
            break;
          case 'entities':
            this.entitiesTabLoaded = true;
            break;
          case 'taxonomy':
            this.taxonomyTabLoaded = true;
            break;
          case 'concept_topics':
            this.conceptsTabLoaded = true;
            break;
          case 'blacklist':
            this.blacklistTabLoaded = true;
            break;
        }
      });
    });

    this.translocoService
      .selectTranslate('configurations.details.title', {
        configuration: this.configuration?.name,
      })
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => {
        if (this.configuration) {
          this.setTitle(translation);
        }
      });

    this.configurationsRoute = `/groups/${this.groupService.getSelectedGroupId(
      true
    )}/configurations`;
  }

  private initConfigData(config: SemantriaConfiguration): SemantriaConfiguration {
    this.translocoService
      .selectTranslate('configurations.details.title', {
        configuration: config.name,
      })
      .pipe(first())
      .subscribe((translation) => this.setTitle(translation));

    config.tags = config.tags || [];
    config.notes = config.notes || '';
    config.config_routes = config.config_routes || [];
    this.hasTags = ConfigurationUtils.hasTags(config);
    config.tags.sort((t1, t2) => t1.localeCompare(t2));
    config.config_routes.sort((r1, r2) => r1.config_route_name.localeCompare(r2.config_route_name));
    return config;
  }

  setTabFragment(tabId: TabId) {
    if (this.isTabDisabled(tabId)) {
      return;
    }

    switch (this.activeTabId) {
      case 'sentiment':
        this.isLoading = !this.sentimentTabLoaded;
        break;
      case 'queries':
        this.isLoading = !this.queriesTabLoaded;
        break;
      case 'entities':
        this.isLoading = !this.entitiesTabLoaded;
        break;
      case 'taxonomy':
        this.isLoading = !this.taxonomyTabLoaded;
        break;
      case 'concept_topics':
        this.isLoading = !this.conceptsTabLoaded;
        break;
      case 'blacklist':
        this.isLoading = !this.blacklistTabLoaded;
        break;
    }

    this.doFragmentNavigation(tabId);
  }

  async interceptTabChange(tabId: TabId) {
    if (this.tabChangeInProgress || this.isTabDisabled(tabId) || this.activeTabId === tabId) {
      return;
    }
    this.tabChangeInProgress = true;
    this.authService.extendSession();

    const promises = [];
    this.configTabs.forEach((c) =>
      promises.push(new Promise((resolve) => c.checkQueryTopicEdit(resolve)))
    );
    const res = await Promise.all(promises);
    if (res.every((r) => r)) {
      this.setTabFragment(tabId);
    }

    this.tabChangeInProgress = false;
  }

  private doFragmentNavigation(tabId: TabId) {
    this.activeTabId = tabId;

    this.router.navigate(['/groups', this.groupId, 'configurations', this.configuration.id], {
      fragment: tabId,
    });
  }

  getTooltipFor(tab: string) {
    if (this.configurationLimits[tab].used === -1) {
      return this.translocoService.translate('configurations.details.tab-not-supported');
    }
    if (this.configurationLayersChanged && tab !== 'layers') {
      return this.translocoService.translate('configurations.details.tab-recalculating');
    }
    return this.translocoService.translate('configurations.details.tab-used-of', {
      used: this.humanizePipe.transform(this.configurationLimits[tab].used),
      total: this.humanizePipe.transform(this.configurationLimits[tab].total),
    });
  }

  isTabDisabled(tabId: TabId) {
    return this.configurationLayersChanged || this.configurationLimits[tabId].used === -1;
  }

  updateLimitUsed(item: string, count: number) {
    this.configurationLimits[item].used += count;

    this.tabs = this.getTabs();
  }

  setLimitUsed(item: string, count: number) {
    this.configurationLimits[item].used = count;

    this.tabs = this.getTabs();
  }

  get summaryLimit() {
    return this.accountLimits.SUMMARY_SIZE_LIMIT;
  }

  canCloneConfiguration(config: SemantriaConfiguration) {
    return (
      (!this.configTabs.length || !this.configTabs.some((c) => c.editMode)) &&
      super.canCloneConfiguration(config)
    );
  }

  canDownloadConfiguration(config: SemantriaConfiguration) {
    return (
      (!this.configTabs.length || !this.configTabs.some((c) => c.editMode)) &&
      super.canDownloadConfiguration(config)
    );
  }

  downloadTaxonomyTooltip(config): string {
    if (!this.configurationLimits.taxonomy.used) {
      return this.translocoService.translate(
        'configurations.details.tooltip-download-taxonomy.not-exist'
      );
    }
    return super.downloadTaxonomyTooltip(config);
  }

  canDownloadTaxonomy(config: SemantriaConfiguration) {
    return (
      (!this.configTabs.length || !this.configTabs.some((c) => c.editMode)) &&
      this.configurationLimits.taxonomy.used &&
      super.canDownloadConfiguration(config)
    );
  }

  onDownloadTaxonomy($event, config: SemantriaConfiguration) {
    const url = this.reportService.getConfigurationTaxonomyNodesReportUrl(config.id, config.name);

    window.location.href = url;

    this.alertService.showToast(
      this.translocoService.translate('configurations.details.download-taxonomy')
    );
  }

  afterConfigurationCloned(clone: SemantriaConfiguration) {
    this.configService.clearCache();
    this.configurations?.push(clone);

    this.router.navigate(['/groups', this.groupId, 'configurations', clone.id]);
  }

  afterConfigurationMoved() {}

  deleteConfigurationTooltip(config) {
    if (this.childConfigs?.length) {
      return this.translocoService.translate(
        'configurations.details.tooltip-delete.part-of-layered'
      );
    }
    return super.deleteConfigurationTooltip(config);
  }

  canDeleteConfiguration(config): boolean {
    return super.canDeleteConfiguration(config) && this.childConfigs?.length === 0;
  }

  beforeConfigurationDeleted() {
    this.configTabs.forEach((c) => c.cancelEdit());
  }

  importConfigurationTooltip() {
    if (this.configuration?.removed) {
      return this.translocoService.translate('configurations.details.tooltip-import.deleted');
    }
    return super.importConfigurationTooltip();
  }

  onTestConfiguration() {
    this.showTestConfigurationDrawer = true;
  }

  onCloseTestConfiguration() {
    this.showTestConfigurationDrawer = false;
  }

  async onImportIntoConfiguration(configDto?, inputFile?) {
    const targetConfigDto = await this.configService.exportConfiguration(this.configuration.id);
    const configType = configDto?.layer ? 'layered' : 'standard';
    this.importConfigData = {
      groupId: this.groupService.getSelectedGroupId(),
      inputFile,
      sourceConfigDto: configDto,
      targetConfigDto,
      configType,
    };

    this.showImportIntoConfiguration = true;
  }

  async onCloseImportIntoConfiguration(result) {
    this.showImportIntoConfiguration = false;

    if (result) {
      if (typeof result === 'boolean') {
        this.onImportIntoConfiguration();
      } else {
        window.location.reload();
      }
    }
  }

  onUploadConfiguration() {
    this.showUploadDialog = true;
  }

  async onCloseUploadConfigurationDialog(res) {
    this.showUploadDialog = false;
    if (res) {
      res.importedItem.notes = res.importedItem.notes || '';
      this.onImportIntoConfiguration(res.importedItem, res.fileItem.file);
    }
  }

  async onTaxonomyTreeSaved() {
    this.taxonomyItemUsage = await this.configService.getTaxonomyItems(this.configuration.id);
  }

  onConfigurationLayersChanged($event) {
    this.configurationLayersChanged = $event;
    this.tabs = this.getTabs();
  }

  onOpenConfigurationOptions() {
    this.showConfigurationOptions = true;
  }

  onCloseConfigurationOptions(obj: {
    configuration: SemantriaConfiguration;
    visibilityOptions: ConfigVisibiltyOptions;
  }) {
    this.showConfigurationOptions = false;
    if (!obj) {
      return;
    }
    this.configuration = this.initConfigData(obj.configuration);
    if (this.localStorageService.getItem('configVisibilityOptions')) {
      const options: ConfigVisibiltyOptions[] =
        this.localStorageService.getItem('configVisibilityOptions');
      const optionIndex = options.findIndex((opt) => opt.id === obj.configuration.id);
      if (optionIndex !== -1) {
        options[optionIndex] = {
          id: obj.configuration.id,
          showConfigDescription: obj.visibilityOptions.showConfigDescription,
          showConfigTags: obj.visibilityOptions.showConfigTags,
        };
      } else {
        options.push({
          id: obj.configuration.id,
          showConfigDescription: obj.visibilityOptions.showConfigDescription,
          showConfigTags: obj.visibilityOptions.showConfigTags,
        });
      }
      this.localStorageService.setItem('configVisibilityOptions', options);
    } else {
      obj.visibilityOptions.id = obj.configuration.id;
      this.localStorageService.setItem('configVisibilityOptions', [obj.visibilityOptions]);
    }
    this.visibilityOptions = this.getVisibilityOptions(obj.configuration);
  }

  private getVisibilityOptions(config: SemantriaConfiguration) {
    const defaultSettings = { showConfigDescription: true, showConfigTags: true };
    const options: ConfigVisibiltyOptions[] =
      this.localStorageService.getItem('configVisibilityOptions');
    if (options) {
      return options.find((opt) => opt.id === config.id) || defaultSettings;
    }
    return defaultSettings;
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }
}
